import axios from "axios";
import { db } from "@/services/db";
import store from "@/store";
import imageCompression from 'browser-image-compression';

const new_axios = axios.create();

export default {
    /**
     * Compress images for inspection photos
     *
     * IMPORTANT: This function ALWAYS outputs JPEG format regardless of input.
     *
     * WHY JPEG-ONLY?
     * ===============
     *
     * 1. Backend Constraint: Backend can only resize/process JPEG images
     * 2. PDF Reports: Inspection reports contain 80+ images - JPEG is standard for photos
     * 3. Primary Use Case: 95%+ of images come from camera captures (already JPEG)
     * 4. Better Compression: JPEG compresses inspection photos far better than PNG
     * 5. Consistent Pipeline: Two-stage compression (frontend 1MB → backend for PDF) requires format consistency
     *
     * ACCEPTABLE TRADE-OFFS:
     * ======================
     *
     * - PNG transparency lost: Not needed for inspection photos (defects, components, observations)
     * - Screenshots/diagrams: Rare in inspection workflow, acceptable quality loss
     * - Gallery PNG selection: Converted to JPEG, but this is edge case (<5% of images)
     *
     * IF THIS BECOMES AN ISSUE:
     * =========================
     *
     * If users need transparency or lossless quality (e.g., architectural diagrams):
     * - Update backend to handle multiple formats (PNG, WebP)
     * - Add format detection: `fileType: blob.type.startsWith('image/png') ? 'image/png' : 'image/jpeg'`
     * - Test PDF generation with mixed formats
     * - Accept higher storage/bandwidth costs (PNGs are 3-5x larger for photos)
     * - Update SyncManager.js format metadata to preserve original format
     *
     * Current implementation prioritizes backend compatibility and optimal compression for photos.
     *
     * @param {string} base64Data - Base64 encoded image data
     * @param {number} maxSizeMB - Maximum file size in MB (default: 1MB)
     * @param {number} targetWidth - Maximum width/height in pixels (default: 1920px)
     * @param {number} minQuality - JPEG quality 0-1 (default: 0.75)
     * @param {boolean} addLabel - Add size/resolution label
     * @param {boolean} cropToSquare - Crop to square aspect ratio
     * @param {boolean} addTimestamp - Add timestamp label
     * @param {boolean} addInspectorName - Add inspector name label
     * @param {string} inspectorName - Name of inspector
     * @returns {Promise<string>} Compressed image as base64 JPEG
     */
    async compress(
        base64Data,
        maxSizeMB = 1,  // Changed from 2 to 1MB
        targetWidth = 1920,
        minQuality = 0.75,  // Changed from 0.8 to 0.75
        addLabel = false,
        cropToSquare = false,
        addTimestamp = false,
        addInspectorName = false,
        inspectorName = null
    ) {
        try {
            console.log(`Starting compression: target ${maxSizeMB}MB, quality ${minQuality}`);

            // Handle crop to square first
            if (cropToSquare) {
                base64Data = await this.cropToSquare(base64Data);
            }

            // Convert base64 to Blob
            const response = await fetch(base64Data);
            const blob = await response.blob();

            // IMPORTANT: Always create JPEG file, regardless of input format
            // See function documentation above for rationale
            const file = new File([blob], 'image.jpg', { type: 'image/jpeg' });

            // Get original size for logging
            const originalSizeMB = file.size / (1024 * 1024);
            console.log(`Original size: ${originalSizeMB.toFixed(2)} MB`);

            // OPTIMIZATION: Skip compression if already below target size
            // This preserves original quality and saves processing time
            if (originalSizeMB <= maxSizeMB) {
                console.log(`✓ Image already below ${maxSizeMB}MB target - skipping compression, preserving original quality`);

                // Use the original base64Data that was passed in - it's already in the format we need
                // No need to keep file/blob references in memory
                let result = base64Data;

                // Add labels if needed
                if (addLabel || addTimestamp || addInspectorName) {
                    // For labels, we need to convert to file temporarily, then immediately release
                    result = await this.addLabelsToImage(
                        file,
                        addLabel,
                        addTimestamp,
                        addInspectorName,
                        inspectorName,
                        originalSizeMB,
                        targetWidth
                    );
                    // file reference released here, addLabelsToImage handles its own cleanup

                    // Validate final size after labels
                    const finalBlob = this.base64ToBlob(result);
                    const finalSizeMB = finalBlob.size / (1024 * 1024);

                    if (finalSizeMB > maxSizeMB) {
                        console.warn(
                            `⚠️ Final image size after labels: ${finalSizeMB.toFixed(2)}MB (exceeds ${maxSizeMB}MB target). ` +
                            `Labels added ~${((finalSizeMB - originalSizeMB) * 1024).toFixed(0)}KB overhead.`
                        );
                    }
                }

                // Return the base64 - original file/blob are garbage collected
                return result;
            }

            // Compression options - JPEG only, 1MB target
            const options = {
                maxSizeMB: maxSizeMB,
                maxWidthOrHeight: targetWidth,
                useWebWorker: true,              // Background thread - keeps UI responsive
                fileType: 'image/jpeg',          // Always JPEG for PDF compatibility
                initialQuality: minQuality,
                alwaysKeepResolution: false,
                maxIteration: 10                 // Reduced from 20: typically enough, better mobile performance
            };

            // Compress using browser-image-compression
            let compressedFile = await imageCompression(file, options);
            let compressedSizeMB = compressedFile.size / (1024 * 1024);

            console.log(`Compression successful: ${compressedSizeMB.toFixed(2)} MB (${((1 - compressedSizeMB / originalSizeMB) * 100).toFixed(1)}% reduction)`);

            // Validate compression result and apply fallback strategy if needed
            if (compressedSizeMB > maxSizeMB) {
                const overage = compressedSizeMB - maxSizeMB;
                const overagePercent = (overage / maxSizeMB) * 100;

                // If overage is less than 20%, accept it with a warning
                if (overagePercent <= 20) {
                    console.warn(
                        `⚠️ Compression slightly exceeded target: ${compressedSizeMB.toFixed(2)}MB vs ${maxSizeMB}MB target (${overagePercent.toFixed(1)}% over). ` +
                        `Accepting result - high-detail inspection photo may require slightly more space.`
                    );
                } else {
                    // Overage is significant (>20%) - try more aggressive compression as fallback
                    console.warn(
                        `⚠️ Compression significantly exceeded target: ${compressedSizeMB.toFixed(2)}MB vs ${maxSizeMB}MB target (${overagePercent.toFixed(1)}% over). ` +
                        `Attempting fallback compression...`
                    );

                    try {
                        // STEP 1: Try reducing dimensions first (preserves quality better for inspection photos)
                        console.log('Fallback Step 1: Reducing dimensions while maintaining quality...');
                        const dimensionFallbackOptions = {
                            ...options,
                            maxSizeMB: maxSizeMB * 0.9,        // Target 10% below limit to have margin
                            maxWidthOrHeight: Math.floor(targetWidth * 0.85), // Smaller dimensions (e.g., 1920 -> 1632)
                            initialQuality: minQuality,        // KEEP original quality
                            maxIteration: 15                   // More iterations for fallback
                        };

                        const dimensionFallbackFile = await imageCompression(file, dimensionFallbackOptions);
                        const dimensionFallbackSizeMB = dimensionFallbackFile.size / (1024 * 1024);

                        if (dimensionFallbackSizeMB <= maxSizeMB) {
                            console.log(`✓ Dimension reduction successful: ${dimensionFallbackSizeMB.toFixed(2)}MB (quality preserved at ${minQuality})`);
                            compressedFile = dimensionFallbackFile;
                            compressedSizeMB = dimensionFallbackSizeMB;
                        } else {
                            // STEP 2: If dimension reduction wasn't enough, try reducing quality too
                            console.log(`Dimension reduction not sufficient (${dimensionFallbackSizeMB.toFixed(2)}MB). Trying quality reduction...`);
                            const qualityFallbackOptions = {
                                ...dimensionFallbackOptions,
                                initialQuality: minQuality * 0.85, // Now reduce quality (e.g., 0.75 -> 0.64)
                            };

                            const qualityFallbackFile = await imageCompression(file, qualityFallbackOptions);
                            const qualityFallbackSizeMB = qualityFallbackFile.size / (1024 * 1024);

                            if (qualityFallbackSizeMB < compressedSizeMB) {
                                console.log(`✓ Quality reduction successful: ${qualityFallbackSizeMB.toFixed(2)}MB (quality: ${(minQuality * 0.85).toFixed(2)})`);
                                compressedFile = qualityFallbackFile;
                                compressedSizeMB = qualityFallbackSizeMB;
                            } else {
                                console.warn(`✗ Fallback compression did not improve size. Using original compression result.`);
                            }
                        }
                    } catch (fallbackError) {
                        console.error('Fallback compression failed:', fallbackError);
                        console.warn('Using original compression result despite size overage.');
                    }
                }
            }

            // Add labels if needed
            if (addLabel || addTimestamp || addInspectorName) {
                const result = await this.addLabelsToImage(
                    compressedFile,
                    addLabel,
                    addTimestamp,
                    addInspectorName,
                    inspectorName,
                    originalSizeMB,
                    targetWidth
                );

                // Validate final size after labels (labels add minimal overhead)
                const finalBlob = this.base64ToBlob(result);
                const finalSizeMB = finalBlob.size / (1024 * 1024);

                if (finalSizeMB > maxSizeMB) {
                    console.warn(
                        `⚠️ Final image size after labels: ${finalSizeMB.toFixed(2)}MB (exceeds ${maxSizeMB}MB target). ` +
                        `Labels added ~${((finalSizeMB - compressedSizeMB) * 1024).toFixed(0)}KB overhead.`
                    );
                }

                return result;
            }

            // Convert back to base64
            const compressedBase64 = await imageCompression.getDataUrlFromFile(compressedFile);
            return compressedBase64;

        } catch (error) {
            console.error('Compression failed:', error);
            // Fallback to original if compression fails
            console.warn('Returning original image due to compression error');
            return base64Data;
        }
    },

    /**
     * Adds labels to compressed images (timestamp, inspector name, size info)
     *
     * IMPORTANT: This function uses DOM APIs (Image, document.createElement) and is
     * intentionally main-thread only. It is called AFTER browser-image-compression
     * completes (which uses Web Workers internally), so the function always runs on
     * the main thread with full DOM access.
     *
     * Why not use OffscreenCanvas/createImageBitmap?
     * - OffscreenCanvas requires iOS 16.4+ (we support older iOS versions)
     * - createImageBitmap has partial support on iOS 15.6-18.1, full on iOS 17.6+
     * - Current approach works on ALL iOS versions (iOS 12+) in Capacitor/WKWebView
     * - No need for worker compatibility since labels are always added on main thread
     *
     * @param {File|Blob} imageFile - Compressed image file
     * @param {boolean} addLabel - Add size/resolution label
     * @param {boolean} addTimestamp - Add timestamp label
     * @param {boolean} addInspectorName - Add inspector name label
     * @param {string} inspectorName - Name of inspector
     * @param {number} originalSizeMB - Original image size before compression
     * @param {number} targetWidth - Target width for scaling labels
     * @returns {Promise<string>} Base64 encoded image with labels
     */
    async addLabelsToImage(
        imageFile,
        addLabel,
        addTimestamp,
        addInspectorName,
        inspectorName,
        originalSizeMB,
        targetWidth
    ) {
        try {
            // Convert file to base64 to load as image
            const base64 = await imageCompression.getDataUrlFromFile(imageFile);

            // Create image element (main thread only - see function docs)
            const img = new Image();
            await new Promise((resolve, reject) => {
                img.onload = resolve;
                img.onerror = reject;
                img.src = base64;
            });

            // Create canvas (main thread only - see function docs)
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');

            // Draw image
            ctx.drawImage(img, 0, 0);

            // Label styling (scale based on targetWidth)
            const scaleFactor = targetWidth / 1920;
            const baseFontSize = 10;
            const baseLabelHeight = 15;
            const basePadding = 5;
            const baseMargin = 3;

            const fontSize = baseFontSize * scaleFactor;
            const labelHeight = baseLabelHeight * scaleFactor;
            const padding = basePadding * scaleFactor;
            const marginBetweenLabels = baseMargin * scaleFactor;

            let yPosition = canvas.height - padding - labelHeight;

            // Timestamp in Nederlands formaat
            const timestamp = new Date().toLocaleString("nl-NL", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
            });

            // Add size/resolution label
            if (addLabel) {
                const label = `Size: ${originalSizeMB.toFixed(2)} MB, Resolution: ${img.width}x${img.height}`;
                ctx.font = `${fontSize}px Arial`;
                const labelWidth = ctx.measureText(label).width;
                const x = padding;
                ctx.fillStyle = "rgba(0, 0, 0, 0.7)";
                ctx.fillRect(x, yPosition, labelWidth + 2 * padding, labelHeight);
                ctx.fillStyle = "white";
                ctx.fillText(label, x + padding, yPosition + labelHeight - 5 * scaleFactor);
                yPosition -= labelHeight + marginBetweenLabels;
            }

            // Add timestamp label
            if (addTimestamp) {
                const timestampLabel = `Gemaakt: ${timestamp}`;
                ctx.font = `${fontSize}px Arial`;
                const labelWidth = ctx.measureText(timestampLabel).width;
                const x = padding;
                ctx.fillStyle = "rgba(0, 0, 0, 0.7)";
                ctx.fillRect(x, yPosition, labelWidth + 2 * padding, labelHeight);
                ctx.fillStyle = "white";
                ctx.fillText(timestampLabel, x + padding, yPosition + labelHeight - 5 * scaleFactor);
                yPosition -= labelHeight + marginBetweenLabels;
            }

            // Add inspector name label
            if (addInspectorName && inspectorName) {
                const inspectorLabel = `Inspecteur: ${inspectorName}`;
                ctx.font = `${fontSize}px Arial`;
                const labelWidth = ctx.measureText(inspectorLabel).width;
                const x = padding;
                ctx.fillStyle = "rgba(0, 0, 0, 0.7)";
                ctx.fillRect(x, yPosition, labelWidth + 2 * padding, labelHeight);
                ctx.fillStyle = "white";
                ctx.fillText(inspectorLabel, x + padding, yPosition + labelHeight - 5 * scaleFactor);
            }

            // Convert canvas to base64 JPEG at quality 1.0
            // Note: Image is already compressed by browser-image-compression
            // Using quality 1.0 avoids double compression artifacts
            const result = canvas.toDataURL('image/jpeg', 1.0);

            // CRITICAL: Clear canvas to free memory immediately
            // Canvas holds full uncompressed pixel data which can be 10-20MB per image
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            canvas.width = 0;
            canvas.height = 0;

            // Clear image element to help garbage collection
            img.src = '';
            img.onload = null;
            img.onerror = null;

            return result;
        } catch (error) {
            console.error('Failed to add labels:', error);
            // Return original if label adding fails
            return await imageCompression.getDataUrlFromFile(imageFile);
        }
    },

    async cropToSquare(base64Data, options = {}) {
        // Default options with quality settings
        const defaults = {
            outputFormat: "image/jpeg",
            quality: 0.95, // Slightly reduced from 1.0 for better file size without visible loss
            smoothing: true, // Enable image smoothing for better quality
            centering: "center", // center, top, bottom, left, right, or custom
            customOffsetX: 0, // Custom offset percentage (-1 to 1)
            customOffsetY: 0, // Custom offset percentage (-1 to 1)
        };

        const settings = { ...defaults, ...options };

        // Handle base64 data properly with error checking
        try {
            // Extract the base64 data part and mime type safely
            const matches = base64Data.match(/^data:([A-Za-z-+/]+);base64,(.+)$/);
            if (!matches || matches.length !== 3) {
                throw new Error("Invalid base64 data format");
            }

            const mimeString = matches[1];
            const byteString = atob(matches[2]);

            // Convert base64 to array buffer
            const ab = new ArrayBuffer(byteString.length);
            const ia = new Uint8Array(ab);
            for (let i = 0; i < byteString.length; i++) {
                ia[i] = byteString.charCodeAt(i);
            }

            const blob = new Blob([ab], { type: mimeString });

            // Create image bitmap with options for better quality
            const bitmap = await createImageBitmap(blob, {
                premultiplyAlpha: "premultiply",
                colorSpaceConversion: "default",
                resizeQuality: "high",
            });

            // Calculate dimensions for square crop
            const squareSize = Math.min(bitmap.width, bitmap.height);

            // Calculate offsets based on centering option
            let offsetX = (bitmap.width - squareSize) / 2; // Default center
            let offsetY = (bitmap.height - squareSize) / 2; // Default center

            // Adjust offsets based on centering preference
            switch (settings.centering) {
                case "top":
                    offsetY = 0;
                    break;
                case "bottom":
                    offsetY = bitmap.height - squareSize;
                    break;
                case "left":
                    offsetX = 0;
                    break;
                case "right":
                    offsetX = bitmap.width - squareSize;
                    break;
                case "custom": {
                    // Apply custom offsets, constrained to valid ranges
                    const maxOffsetX = bitmap.width - squareSize;
                    const maxOffsetY = bitmap.height - squareSize;
                    offsetX = Math.max(0, Math.min(maxOffsetX, offsetX + (settings.customOffsetX * maxOffsetX) / 2));
                    offsetY = Math.max(0, Math.min(maxOffsetY, offsetY + (settings.customOffsetY * maxOffsetY) / 2));
                    break;
                }
            }

            // Create canvas with proper size
            const canvas = new OffscreenCanvas(squareSize, squareSize);
            const ctx = canvas.getContext("2d", { alpha: true });

            // Configure rendering quality
            ctx.imageSmoothingEnabled = settings.smoothing;
            ctx.imageSmoothingQuality = "high";

            // Clear canvas before drawing (important for transparent images)
            ctx.clearRect(0, 0, squareSize, squareSize);

            // Draw the image with high quality settings
            ctx.drawImage(bitmap, offsetX, offsetY, squareSize, squareSize, 0, 0, squareSize, squareSize);

            // Convert to blob with specified format and quality
            const croppedBlob = await canvas.convertToBlob({
                type: settings.outputFormat,
                quality: settings.quality,
            });

            // CRITICAL: Clear canvas to free memory immediately
            ctx.clearRect(0, 0, squareSize, squareSize);
            canvas.width = 0;
            canvas.height = 0;

            // Close the bitmap to free memory
            bitmap.close();

            // Convert blob to base64
            const result = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    // Clear reader references
                    reader.onloadend = null;
                    reader.onerror = null;
                    resolve(reader.result);
                };
                reader.onerror = () => {
                    reader.onloadend = null;
                    reader.onerror = null;
                    reject(new Error("Failed to convert blob to base64"));
                };
                reader.readAsDataURL(croppedBlob);
            });

            return result;
        } catch (error) {
            throw new Error(`Image cropping failed: ${error.message}`);
        }
    },

    async fileToBase64(file) {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                const result = reader.result;
                // Clear reader references to help garbage collection
                reader.onloadend = null;
                reader.onerror = null;
                resolve(result);
            };
            reader.readAsDataURL(file);
        });
    },
    toFormData(obj) {
        let formData = new FormData();
        for (let key in obj) {
            if (obj[key]) {
                formData.append(key, obj[key]);
            }
        }

        return formData;
    },
    blobToBase64(blob) {
        if (!blob) {
            return;
        }
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
        });
    },
    base64ToBlob(base64) {
        const byteString = atob(base64.split(",")[1]);
        const mimeString = base64.split(",")[0].split(":")[1].split(";")[0];
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }
        return new Blob([ab], { type: mimeString });
    },
    base64ToFile(base64, filename) {
        const byteString = atob(base64.split(",")[1]);
        const mimeString = base64.split(",")[0].split(":")[1].split(";")[0];
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }
        const blob = new Blob([ab], { type: mimeString });
        return new File([blob], filename, { type: mimeString });
    },
    async getFileData(file) {
        if (!file.url) return null;

        // Fetch het bestand en converteer het naar een Base64-string of ArrayBuffer
        const response = await fetch(file.url);
        const fileBlob = await response.blob();

        return await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(fileBlob); // Voor Base64
        });
    },
    async getImageData(image) {
        const image_in_db = await db.images.where({ uuid: image.uuid }).first();

        if (image_in_db) {
            // if no blob in db, fetch it
            if (!image_in_db.blob) {
                let blob = await this.fetchBlob(image.url);
                await db.images.update(image.uuid, { blob: blob });
            } else if (image_in_db.updated_at !== image.updated_at) {
                // Haal nieuwe afbeelding op en sla deze op als BLOB
                let blob = await this.fetchBlob(image.url);

                // Update database met nieuwe BLOB en `updated_at`
                await db.images.put({
                    uuid: image.uuid,
                    url: image.url, // URL blijft behouden
                    blob: blob, // Nieuwe afbeelding als BLOB
                    title: image.title,
                    updated_at: image.updated_at,
                });
            }
        } else {
            // Afbeelding staat nog niet in IndexedDB, dus opslaan
            let blob = await this.fetchBlob(image.url);

            await db.images.put({
                uuid: image.uuid,
                url: image.url, // URL blijft behouden
                blob: blob, // Afbeelding als BLOB opslaan
                title: image.title,
                updated_at: image.updated_at,
            });
        }
    },
    async fetchBlob(imageUrl) {
        try {
            let response = await fetch(imageUrl);
            let blob = await response.blob();
            return blob;
        } catch (error) {
            console.error("Fout bij ophalen afbeelding:", error);
            return null;
        }
    },
    checkType(obj) {
        return Object.prototype.toString.call(obj).slice(8, -1);
    },
    checkIfUrlOrBase64(url) {
        if (url.includes("data:image")) {
            return "Base64";
        } else {
            return "Url";
        }
    },
    async returnBase64(url) {
        const response = await new_axios.get(url, {
            responseType: "blob",
        });

        if (response.status === 403) {
            console.log("Afbeelding bestaat niet meer in backend (403).");
            return null; // of een andere manier om aan te geven dat de afbeelding niet moet worden toegevoegd
        }

        if (response.status !== 200) {
            return;
        }

        // return base64 string
        return await new Promise((resolve) => {
            const reader = new FileReader();
            reader.readAsDataURL(response.data);
            reader.onloadend = () => {
                const base64data = reader.result;
                resolve(base64data);
            };
        });
    },
    smartNumber(name, number) {
        // Check if the name starts or ends with a number
        const startNumberRegex = /^(\d+)/;
        const endNumberRegex = /(\d+)$/;

        let newName = name;

        // Replace the number at the start or end with the new number
        if (startNumberRegex.test(name)) {
            newName = newName.replace(startNumberRegex, number);
        } else if (endNumberRegex.test(name)) {
            newName = newName.replace(endNumberRegex, number);
        } else {
            // If the name doesn't have a number at the start or end, add the new number with a space before it
            newName += " " + number;
        }

        return newName;
    },
    async defineMeasurementRules(rules) {
        if (!rules) {
            return;
        }

        if (rules.fields) {
            for (let i = 0; i < rules.fields.length; i++) {
                if (rules.fields[i].rules) {
                    rules.fields[i].rules = await this.defineMeasurementRules(rules.fields[i].rules);
                }
            }
        }

        return rules;
    },
};
